import { memo, useMemo } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useArtifactContext } from '../lib/artifactContext';
import { extractArtifactsFromText, isArtifactKind, workspaceRelativePath } from '../lib/artifacts';
import { useAuth } from '../lib/auth';
import { ArtifactCard } from './ArtifactCard';
import { CodeBlock } from './CodeBlock';

/**
 * Markdown renderer for assistant prose. Supports GFM (tables, task
 * lists, strikethrough) and passes fenced code blocks through
 * shiki. Inline code uses the `.prose-inline-code` class defined
 * in base.css so colors stay on the token palette.
 *
 * Memoised: the parent rebuilds this on every streaming delta but
 * only the tail message's text actually changes. React.memo skips
 * the re-parse + re-render for the stable history below.
 *
 * Links open in a new tab; we never render raw HTML from the model.
 */
export const MarkdownContent = memo(function MarkdownContent({
  children,
  messageId,
}: {
  children: string;
  messageId: string;
}) {
  const { activeId, onOpen, onOpenFile } = useArtifactContext();
  const { user } = useAuth();
  const username = user?.username ?? '';
  // Artifact extraction is non-trivial (regex scan). Keep it inside
  // a memo keyed on the body so a stable history message doesn't
  // re-scan when context changes.
  const artifacts = useMemo(
    () => extractArtifactsFromText(children, messageId),
    [children, messageId],
  );
  return (
    <div className="mc-prose">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          code({ className, children }) {
            const text = Array.isArray(children) ? children.join('') : String(children ?? '');
            const langMatch = /language-([\w-]+)/.exec(className ?? '');
            if (!langMatch) {
              return <code className="prose-inline-code">{text}</code>;
            }
            const lang = langMatch[1] ?? '';
            if (isArtifactKind(lang, text)) {
              // Same body + lang → same artifact id. Match by body to
              // pair the block with its extracted-artifact metadata.
              const match = artifacts.find((a) => a.body === text);
              if (match) {
                return (
                  <ArtifactCard artifact={match} onOpen={onOpen} isOpen={activeId === match.id} />
                );
              }
            }
            return <CodeBlock code={text} lang={lang} />;
          },
          pre({ children }) {
            // Unwrap — CodeBlock + ArtifactCard own their framing.
            return <>{children}</>;
          },
          ...TABLE_COMPONENTS,
          a({ children, href }) {
            const relativePath = workspaceRelativePath(
              href ?? '',
              username,
              window.location.origin,
            );
            if (relativePath) {
              return (
                <a
                  href={`/api/files/download?path=${encodeURIComponent(relativePath)}`}
                  onClick={(event) => {
                    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                    event.preventDefault();
                    onOpenFile(relativePath);
                  }}
                  className="text-primary underline decoration-dotted underline-offset-2 hover:opacity-80"
                >
                  {children}
                </a>
              );
            }
            return (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline decoration-dotted underline-offset-2 hover:opacity-80"
              >
                {children}
              </a>
            );
          },
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
});

const TABLE_COMPONENTS: Components = {
  table({ children }) {
    return (
      <div className="my-3 overflow-x-auto">
        <table className="border-collapse text-sm">{children}</table>
      </div>
    );
  },
  th({ children }) {
    return (
      <th className="border border-border bg-subtle px-2.5 py-1 text-left font-semibold">
        {children}
      </th>
    );
  },
  td({ children }) {
    return <td className="border border-border px-2.5 py-1">{children}</td>;
  },
};
